import { createStore } from "./createStore";
import type { AgentProfile, PersistedTerminal, TerminalMeta } from "../types";

export interface TerminalsState {
  agents: AgentProfile[];
  terminals: TerminalMeta[];
  // Persisted terminal records from previous .app sessions. The continuity
  // service consumes this as an internal restore queue; it is not rendered as
  // placeholder UI in the terminal pane.
  persistedTerminals: PersistedTerminal[];
  // Pending replay payload for a terminal id. When an interrupted exited
  // record is respawned, the transcript is pre-fetched here so TerminalHost
  // can write it into the xterm before the WebSocket opens.
  replayScrollback: Record<string, string>;
  // Set true the moment the user explicitly closes any terminal in this
  // page-load. The Terminal-button auto-reattach checks this to know that
  // session.openTerminals ids are stale and should not be reattached.
  userClosedAny: boolean;
  activeId: string | null;
  spawning: boolean;
  error: string | null;
  // UI pane visibility. Lives on the singleton store (not WorkbenchView local
  // state) so it survives any WorkbenchView remount within the same JS
  // session — route switches, Tauri webview reopen with the process still
  // alive, StrictMode double-mount, etc. Without this, navigating to the
  // Security Room and back would reset the pane to closed even though the
  // terminals were still alive in the store and on the backend.
  paneVisible: boolean;
  paneMaximized: boolean;
  // User-assigned per-terminal display names, keyed by terminal id. Persisted
  // to localStorage so a rename survives a reload / app-restart (same scope as
  // the terminal session itself — a reboot fresh-spawns a new id, which gets
  // its own default name). Falls back to the agent name when unset.
  titles: Record<string, string>;
}

// Exported so tests can target the exact key the store writes to. Don't
// hard-code the literal in test files — they'll silently drift if this
// key is ever renamed for a migration.
export const PANE_VISIBLE_KEY = "agentguard-terminal-pane-visible";
export const TERMINAL_TITLES_KEY = "agentguard-terminal-titles";

function readPersistedPaneVisible(): boolean {
  try {
    return localStorage.getItem(PANE_VISIBLE_KEY) === "true";
  } catch {
    return false;
  }
}

function readPersistedTitles(): Record<string, string> {
  try {
    const raw = localStorage.getItem(TERMINAL_TITLES_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? (parsed as Record<string, string>) : {};
  } catch {
    return {};
  }
}

export const terminalsStore = createStore<TerminalsState>({
  agents: [],
  terminals: [],
  persistedTerminals: [],
  replayScrollback: {},
  userClosedAny: false,
  activeId: null,
  spawning: false,
  error: null,
  paneVisible: readPersistedPaneVisible(),
  paneMaximized: false,
  titles: readPersistedTitles(),
});

// Persist paneVisible to localStorage so the pane reopens at the user's
// last choice across full page reloads (not just remounts). paneMaximized
// is deliberately session-only — maximized is a temporary "focus mode"
// state, not a long-lived preference.
let lastPersistedPaneVisible = readPersistedPaneVisible();
terminalsStore.subscribe(() => {
  const current = terminalsStore.getSnapshot().paneVisible;
  if (current === lastPersistedPaneVisible) return;
  lastPersistedPaneVisible = current;
  try {
    localStorage.setItem(PANE_VISIBLE_KEY, current ? "true" : "false");
  } catch {
    // hardened webviews may block localStorage; in-session selection still works.
  }
});

// Persist titles to localStorage the same way paneVisible is persisted above —
// via a store subscriber — so the setState updater stays a pure reducer with no
// I/O side-effect. Reference-equality is a reliable change check because every
// titles mutation (setTerminalTitle, close cleanup) builds a fresh object;
// unrelated setState calls preserve the same titles reference and are skipped.
let lastPersistedTitles = terminalsStore.getSnapshot().titles;
terminalsStore.subscribe(() => {
  const current = terminalsStore.getSnapshot().titles;
  if (current === lastPersistedTitles) return;
  lastPersistedTitles = current;
  try {
    localStorage.setItem(TERMINAL_TITLES_KEY, JSON.stringify(current));
  } catch {
    // hardened webviews may block localStorage; in-session rename still works.
  }
});

export function setTerminalPaneVisible(visible: boolean) {
  terminalsStore.setState({ paneVisible: visible });
}

export function setTerminalPaneMaximized(maximized: boolean) {
  terminalsStore.setState({ paneMaximized: maximized });
}

// Rename a terminal (or clear the override with an empty string). The
// localStorage write-through happens in the subscriber above, so this stays a
// pure state update. Empty/blank names are dropped so the tile falls back to
// the agent name rather than showing a blank title.
export function setTerminalTitle(id: string, title: string) {
  terminalsStore.setState((current) => {
    const next = { ...current.titles };
    const trimmed = title.trim();
    if (trimmed) next[id] = trimmed;
    else delete next[id];
    return { ...current, titles: next };
  });
}
